<script setup lang="ts">
import type { DetailNetAssociation } from "~/utils/netAssociationEstime";

/**
 * PaySummaryDetails — bloc desktop "Détails du don" de /pay/[slug].
 *
 * Rend en colonne :
 *   - "Org reçoit" (montant net)
 *   - Objectif de collecte (DonateGoalProgress ou montant collecté seul)
 *   - Sous-total + frais PSP/Donoor couverts + "Soutenir Donoor"
 *   - Champ message optionnel (si activé)
 *   - Total dû
 *
 * Factorisé hors de /pay/[slug].vue pour réduire la taille du template.
 * Composant 100% présentation : pas d'état interne, v-model uniquement
 * pour le message.
 */

interface Props {
  organizationName: string;
  finalAmount: number;
  /**
   * Ce que l'association reçoit (`net`) : le don, MOINS les frais quand le
   * donateur ne les couvre pas, et le détail de ce qui est retiré
   * (utils/netAssociationEstime.ts). Le sous-total reste le don choisi.
   */
  detailAssociation: DetailNetAssociation;
  finalAmountWithDiscount: number;
  /** Montant pré-formatté pour le total dû ("12 €" ou "—"). */
  displayAmount: string;
  formatAmount: (amount: number) => string;
  includeFees: boolean;
  stripeFeesAmount: number;
  donoorFeesAmount: number;
  /**
   * PSP qui encaissera réellement : on n'écrit pas « Stripe » quand l'org
   * encaisse via PayPal. Même convention que DonateFeesOption.
   */
  providerLabel?: "stripe" | "paypal";
  donateUs: boolean;
  donateToUsAmount: number;
  isSubscription: boolean;
  subscriptionSuffix: string;
  showGoalProgress: boolean;
  goalAmount: number | null;
  raisedAmount: number;
  primaryColor: string | null;
  daysRemaining: number | null;
  /** Active l'affichage du champ message (optionnel). */
  isMessageEnabled: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  providerLabel: "stripe",
});

const providerDisplayName = computed(() =>
  props.providerLabel === "paypal" ? "PayPal" : "Stripe",
);

const message = defineModel<string>("message", { required: true });

const { t } = useI18n();
</script>

<template>
  <div>
    <!-- "Organisation reçoit" -->
    <div class="space-y-4 mb-6">
      <div class="flex justify-between items-center py-2">
        <span class="text-gray-700">{{ t('don.detailsRecapitulatif.organisationRecoit', { organisation: organizationName }) }}</span>
        <span class="font-medium text-gray-900">
          {{ detailAssociation.net > 0 ? formatAmount(detailAssociation.net) : "—" }}
        </span>
      </div>
      <DonateDeductionsAssociation
        :detail="detailAssociation"
        :prestataire="providerLabel"
        :format-amount="formatAmount"
      />
    </div>

    <!-- Objectif de collecte -->
    <div v-if="showGoalProgress" class="mb-6">
      <!-- Variante "objectif défini" : barre via composant partagé -->
      <DonateGoalProgress
        v-if="goalAmount"
        :raised-amount="raisedAmount"
        :goal-amount="goalAmount"
        :primary-color="primaryColor"
        :format-amount="formatAmount"
        :days-remaining="daysRemaining"
      />
      <!-- Variante "pas d'objectif chiffré" : montant collecté seul -->
      <div v-else class="p-4 bg-gray-50 rounded-xl">
        <div class="flex justify-between items-end">
          <span class="text-base font-bold text-gray-900">
            {{ formatAmount(raisedAmount) }}
          </span>
          <span class="text-gray-500 text-sm">{{ t('don.detailsRecapitulatif.collectes') }}</span>
        </div>
      </div>
    </div>

    <!-- Sous-total + frais + Soutenir Donoor + Total -->
    <div class="border-t border-gray-200 pt-4">
      <div class="flex justify-between items-center py-2">
        <span class="text-gray-700">{{ t('don.detailsRecapitulatif.sousTotal') }}</span>
        <span class="font-medium text-gray-900">
          {{ finalAmount > 0 ? formatAmount(finalAmount) : "—" }}
        </span>
      </div>

      <div
        v-if="includeFees && stripeFeesAmount > 0"
        class="flex justify-between items-center py-2 text-sm"
      >
        <span class="text-gray-500">{{ t('don.detailsRecapitulatif.fraisCouverts', { provider: providerDisplayName }) }}</span>
        <span class="text-gray-500">+ {{ formatAmount(stripeFeesAmount) }}</span>
      </div>

      <div
        v-if="includeFees && donoorFeesAmount > 0"
        class="flex justify-between items-center py-2 text-sm"
      >
        <span class="text-gray-500">{{ t('don.detailsRecapitulatif.fraisDonoorCouverts') }}</span>
        <span class="text-gray-500">+ {{ formatAmount(donoorFeesAmount) }}</span>
      </div>

      <div
        v-if="donateUs && donateToUsAmount > 0"
        class="flex justify-between items-center py-2 text-sm"
      >
        <span class="text-gray-500">{{ t('don.detailsRecapitulatif.soutenirDonoor') }}</span>
        <span class="text-gray-500">+ {{ formatAmount(donateToUsAmount) }}</span>
      </div>

      <!-- Message optionnel (desktop uniquement) — border et mx retirés du
           wrapper (l'input gère sa propre border). Padding interne `p-px`
           sur l'UTextarea pour offrir un léger respire visuel au focus ring. -->
      <div v-if="isMessageEnabled" class="mt-4 bg-white">
        <label class="text-sm font-medium text-gray-700 mb-1.5 block">
          {{ t('don.detailsRecapitulatif.messageOptionnel') }}
        </label>
        <UTextarea
          v-model="message"
          :placeholder="t('don.detailsRecapitulatif.placeholderMessageEncouragement')"
          :rows="2"
          class="w-full"
          :ui="{
            base: 'rounded-2xl! px-4 py-3 leading-relaxed bg-white! text-gray-900! placeholder:text-gray-400! ring-1 ring-gray-200! focus:ring-2 focus:ring-blue-500!',
          }"
        />
      </div>

      <div
        class="flex justify-between items-center py-3 border-t border-gray-200 mt-4"
      >
        <span class="text-lg font-semibold text-gray-900">
          {{ t('don.detailsRecapitulatif.montantTotalDu') }}
        </span>
        <span class="text-lg font-bold text-gray-900">
          {{ displayAmount }}
          <span
            v-if="isSubscription"
            class="text-sm font-normal text-gray-500"
          >/{{ subscriptionSuffix }}</span>
        </span>
      </div>
    </div>
  </div>
</template>
